import React, { useState } from 'react';
import Icon from 'components/AppIcon';

const AddUserModal = ({ onClose, onAddUser }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    role: '',
    department: '',
    status: 'active',
    permissions: []
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const roles = [
    { value: 'Администратор', label: 'Администратор', permissions: ['admin', 'user_management', 'security_management'] },
    { value: 'Менеджер', label: 'Менеджер', permissions: ['user_management'] },
    { value: 'Модератор', label: 'Модератор', permissions: ['user_management'] },
    { value: 'Пользователь', label: 'Пользователь', permissions: [] }
  ];

  const departments = [
    'IT Отдел',
    'Продажи',
    'Маркетинг',
    'Поддержка',
    'Финансы',
    'HR'
  ];

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Имя обязательно для заполнения';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email обязателен для заполнения';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Некорректный формат email';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Телефон обязателен для заполнения';
    }

    if (!formData.role) {
      newErrors.role = 'Роль обязательна для выбора';
    }

    if (!formData.department) {
      newErrors.department = 'Отдел обязателен для выбора';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const selectedRole = roles.find(r => r.value === formData.role);
      const userData = {
        ...formData,
        permissions: selectedRole?.permissions || []
      };
      
      onAddUser(userData);
    } catch (error) {
      console.error('Error adding user:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-surface rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-secondary-200">
          <div>
            <h2 className="text-xl font-semibold text-text-primary">
              Добавить нового пользователя
            </h2>
            <p className="text-sm text-text-secondary mt-1">
              Заполните информацию о новом пользователе системы
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-md hover:bg-secondary-100 transition-colors duration-150"
          >
            <Icon name="X" size={20} className="text-secondary-500" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Personal Information */}
          <div>
            <h3 className="text-lg font-medium text-text-primary mb-4">
              Личная информация
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Полное имя *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className={`
                    text-text-primary bg-secondary-50
                    w-full px-3 py-2 border rounded-md transition-all duration-150
                    focus:outline-none focus:ring-2 focus:ring-primary/20
                    ${errors.name 
                      ? 'border-error focus:border-error' :'border-secondary-200 focus:border-primary'
                    }
                  `}
                  placeholder="Введите полное имя"
                />
                {errors.name && (
                  <p className="text-sm text-error mt-1">{errors.name}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Email адрес *
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className={`
                    text-text-primary bg-secondary-50
                    w-full px-3 py-2 border rounded-md transition-all duration-150
                    focus:outline-none focus:ring-2 focus:ring-primary/20
                    ${errors.email 
                      ? 'border-error focus:border-error' :'border-secondary-200 focus:border-primary'
                    }
                  `}
                  placeholder="user@company.ru"
                />
                {errors.email && (
                  <p className="text-sm text-error mt-1">{errors.email}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Телефон *
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  className={`
                    text-text-primary bg-secondary-50
                    w-full px-3 py-2 border rounded-md transition-all duration-150
                    focus:outline-none focus:ring-2 focus:ring-primary/20
                    ${errors.phone 
                      ? 'border-error focus:border-error' :'border-secondary-200 focus:border-primary'
                    }
                  `}
                  placeholder="+7 (495) 123-45-67"
                />
                {errors.phone && (
                  <p className="text-sm text-error mt-1">{errors.phone}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Отдел *
                </label>
                <div className="relative">
                  <select
                    value={formData.department}
                    onChange={(e) => handleInputChange('department', e.target.value)}
                    className={`
                      text-text-primary bg-secondary-50
                      w-full px-3 py-2 border rounded-md appearance-none transition-all duration-150
                      focus:outline-none focus:ring-2 focus:ring-primary/20
                      ${errors.department 
                        ? 'border-error focus:border-error' :'border-secondary-200 focus:border-primary'
                      }
                    `}
                  >
                    <option value="">Выберите отдел</option>
                    {departments.map(dept => (
                      <option key={dept} value={dept}>{dept}</option>
                    ))}
                  </select>
                  <Icon 
                    name="ChevronDown" 
                    size={16} 
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-secondary-400 pointer-events-none" 
                  />
                </div>
                {errors.department && (
                  <p className="text-sm text-error mt-1">{errors.department}</p>
                )}
              </div>
            </div>
          </div>

          {/* Role and Permissions */}
          <div>
            <h3 className="text-lg font-medium text-text-primary mb-4">
              Роль и права доступа
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Роль *
                </label>
                <div className="relative">
                  <select
                    value={formData.role}
                    onChange={(e) => handleInputChange('role', e.target.value)}
                    className={`
                      text-text-primary bg-secondary-50
                      w-full px-3 py-2 border rounded-md appearance-none transition-all duration-150
                      focus:outline-none focus:ring-2 focus:ring-primary/20
                      ${errors.role 
                        ? 'border-error focus:border-error' :'border-secondary-200 focus:border-primary'
                      }
                    `}
                  >
                    <option value="">Выберите роль</option>
                    {roles.map(role => (
                      <option key={role.value} value={role.value}>{role.label}</option>
                    ))}
                  </select>
                  <Icon 
                    name="ChevronDown" 
                    size={16} 
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-secondary-400 pointer-events-none" 
                  />
                </div>
                {errors.role && (
                  <p className="text-sm text-error mt-1">{errors.role}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Статус
                </label>
                <div className="relative">
                  <select
                    value={formData.status}
                    onChange={(e) => handleInputChange('status', e.target.value)}
                    className="
                      text-text-primary bg-secondary-50
                      w-full px-3 py-2 border border-secondary-200 rounded-md appearance-none
                      focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary
                      transition-all duration-150
                    "
                  >
                    <option value="active">Активен</option>
                    <option value="inactive">Неактивен</option>
                    <option value="pending">Ожидает активации</option>
                  </select>
                  <Icon 
                    name="ChevronDown" 
                    size={16} 
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-secondary-400 pointer-events-none" 
                  />
                </div>
              </div>
            </div>

            {/* Permissions Preview */}
            {formData.role && (
              <div className="mt-4 p-4 bg-secondary-50 rounded-md">
                <p className="text-sm font-medium text-text-primary mb-2">
                  Права доступа для роли "{formData.role}":
                </p>
                <div className="flex flex-wrap gap-2">
                  {roles.find(r => r.value === formData.role)?.permissions.map(permission => (
                    <span
                      key={permission}
                      className="inline-flex items-center px-2 py-1 bg-primary-100 text-primary-700 rounded-full text-xs font-medium"
                    >
                      {permission}
                    </span>
                  )) || (
                    <span className="text-sm text-text-secondary">Базовые права пользователя</span>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-secondary-200">
            <button
              type="button"
              onClick={onClose}
              className="
                px-4 py-2 border border-secondary-200 text-text-secondary rounded-md
                hover:bg-secondary-50 transition-colors duration-150
              "
            >
              Отмена
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="
                flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-md
                hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed
                transition-colors duration-150 hover-lift
              "
            >
              {isSubmitting && (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              )}
              {isSubmitting ? 'Создание...' : 'Создать пользователя'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddUserModal;